'use client'

import * as React from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { toast } from 'sonner'
import {
  BellIcon,
  CircleUserRoundIcon,
  EllipsisVerticalIcon,
  GlobeIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
} from 'lucide-react'
import { useTheme } from 'next-themes'

import { AccountSettingsDialog } from '@/components/account-settings-dialog'
import { NotificationsDialog } from '@/components/notifications-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { UserAvatar } from '@/components/user-avatar'
import { useAuthContext } from '@/hooks/use-auth-context'
import { useMounted } from '@/hooks/use-mounted'
import { useNotificationUnreadCount } from '@/hooks/use-notification-poll'
import { useUiLocaleSync } from '@/hooks/use-ui-locale-sync'
import { locales, type Locale } from '@/i18n/config'
import { localeLabels, setLocaleCookie } from '@/lib/locale'
import { THEME_OPTIONS } from '@/providers/theme-provider'

type NavUserProps = {
  name: string
  email: string
  iconOnly?: boolean
}

// Link error codes surfaced under the `AccountSettings.linkError` namespace.
// `cannot_unlink_last_method` is handled separately via `unlinkLastMethod`.
const KNOWN_LINK_ERRORS = [
  'identity_already_linked',
  'provider_already_linked',
  'email_unverified',
  'generic',
] as const
type LinkErrorCode = (typeof KNOWN_LINK_ERRORS)[number]

const THEME_ICONS = {
  light: SunIcon,
  dark: MoonIcon,
  system: MonitorIcon,
} as const

export function NavUser({ name, email, iconOnly = false }: NavUserProps) {
  const t = useTranslations('NavUser')
  const { isMobile } = useSidebar()
  const { user, signOut } = useAuthContext()
  const currentLocale = useLocale() as Locale
  const { theme = 'light', setTheme } = useTheme()
  const ThemeIcon =
    THEME_ICONS[theme as keyof typeof THEME_ICONS] ?? THEME_ICONS.light
  useUiLocaleSync()
  const mounted = useMounted()
  const [accountOpen, setAccountOpen] = React.useState(false)
  const [notificationsOpen, setNotificationsOpen] = React.useState(false)
  const tAccount = useTranslations('AccountSettings')
  const tNotifications = useTranslations('Notifications.bell')
  // The bell lives in this menu now, so its unread count has to be visible
  // from the closed trigger — otherwise a finished run would be announced
  // nowhere in the chrome.
  const { unreadCount } = useNotificationUnreadCount()

  // After a Google account-link round trip, the BFF `link-finish` route bounces
  // back to the current page with `?account=1` plus either `linked=google`
  // (success) or `link_error=<code>` (failure). Reopen the account dialog,
  // surface the matching toast, then strip these params so a refresh does not
  // re-trigger.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('account') !== '1') {
      return
    }
    setAccountOpen(true)

    if (params.get('linked') === 'google') {
      toast.success(tAccount('linkedToast'))
    }

    const linkError = params.get('link_error')
    if (linkError) {
      if (linkError === 'cannot_unlink_last_method') {
        toast.error(tAccount('unlinkLastMethod'))
      } else {
        const code: LinkErrorCode = (
          KNOWN_LINK_ERRORS as readonly string[]
        ).includes(linkError)
          ? (linkError as LinkErrorCode)
          : 'generic'
        toast.error(tAccount(`linkError.${code}`))
      }
    }

    params.delete('account')
    params.delete('linked')
    params.delete('link_error')
    const query = params.toString()
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', nextUrl)
    // Mount-only: params are consumed and cleared here, so it must run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const imageUrl = user?.avatarUrl ?? undefined
  const avatarSeed = email || user?.id || name

  const handleSignOut = () => {
    signOut('/sign-in')
  }

  // Unread badge on the closed trigger: the count itself is spelled out on
  // the menu row, so this only has to say "something is waiting in here".
  const unreadDot =
    unreadCount > 0 ? (
      <span
        aria-hidden
        className="bg-primary ring-sidebar absolute -end-0.5 -top-0.5 size-2 rounded-full ring-2"
      />
    ) : null

  const triggerButton = iconOnly ? (
    <button
      type="button"
      aria-label="Account menu"
      className="hover:bg-sidebar-hover data-[state=open]:bg-sidebar-hover relative flex size-9 items-center justify-center rounded-md transition-colors"
    >
      <UserAvatar
        seed={avatarSeed}
        name={name}
        avatarUrl={imageUrl}
        className="size-7"
      />
      {unreadDot}
    </button>
  ) : (
    <SidebarMenuButton className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground h-auto py-2">
      <span className="relative inline-flex shrink-0">
        <UserAvatar
          seed={avatarSeed}
          name={name}
          avatarUrl={imageUrl}
          className="h-8 w-8"
        />
        {unreadDot}
      </span>
      <div className="flex min-w-0 flex-1 flex-col text-sm">
        <span className="truncate font-medium tracking-tight">{name}</span>
        <span className="text-muted-foreground truncate text-xs tracking-tight">
          {email}
        </span>
      </div>
      <EllipsisVerticalIcon className="text-muted-foreground ms-auto size-4 shrink-0" />
    </SidebarMenuButton>
  )

  const triggerWrapper = iconOnly
    ? (node: React.ReactNode) => <>{node}</>
    : (node: React.ReactNode) => <SidebarMenuItem>{node}</SidebarMenuItem>

  // Skip Radix dropdown until mounted to avoid SSR/CSR `useId()` mismatches.
  if (!mounted) {
    return triggerWrapper(triggerButton)
  }

  return triggerWrapper(
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="flex items-center gap-2 py-2 font-normal">
            <UserAvatar
              seed={avatarSeed}
              name={name}
              avatarUrl={imageUrl}
              className="size-8"
            />
            <div className="flex min-w-0 flex-1 flex-col text-sm">
              <span className="truncate font-medium tracking-tight">
                {name}
              </span>
              <span className="text-muted-foreground truncate text-xs tracking-tight">
                {email}
              </span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => setNotificationsOpen(true)}>
              <BellIcon />
              {tNotifications('header')}
              {unreadCount > 0 ? (
                <span className="bg-primary text-primary-foreground ms-auto flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAccountOpen(true)}>
              <CircleUserRoundIcon />
              {t('account')}
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <GlobeIcon />
                {t('language')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {locales.map(loc => (
                  <DropdownMenuItem
                    key={loc}
                    onSelect={() => setLocaleCookie(loc)}
                    className={loc === currentLocale ? 'font-semibold' : ''}
                  >
                    {localeLabels[loc]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <ThemeIcon />
                {t('theme')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                  {THEME_OPTIONS.map(option => {
                    const Icon = THEME_ICONS[option]
                    return (
                      <DropdownMenuRadioItem key={option} value={option}>
                        <Icon />
                        {t(`themeOptions.${option}`)}
                      </DropdownMenuRadioItem>
                    )
                  })}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={handleSignOut}>
            <LogOutIcon className="rtl:-scale-x-100" />
            {t('signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AccountSettingsDialog open={accountOpen} onOpenChange={setAccountOpen} />
      <NotificationsDialog
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
      />
    </>,
  )
}
